import React, { forwardRef } from "react";
import { useBookingEditText } from "../text";
import { GoTriangleDown } from "react-icons/go";
import Select, { components } from "react-select";
import SelectionButton from "./SelectionButton";
import CounterCard from "./CounterCard";
import { createTypographyHelper } from "../../../../utils/typography";
import {
  OPT_OUT_LABEL_DRIVER_MEET,
  OPT_OUT_LABEL_NONE,
  getAdminMeetAssistPolicy,
} from "../../../../utils/meetAssist";

// This screen's translation keys are camelCase, where the shared rules use the
// semantic token.
const OPT_OUT_TRANSLATION_KEY = {
  [OPT_OUT_LABEL_NONE]: "none",
  [OPT_OUT_LABEL_DRIVER_MEET]: "driverMeet",
};


const TRAIN_GOLD_ICON = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/icons/train_gold.svg";

const TERMINAL_HANEDA_OPTIONS = [
  { label: "Terminal 1 (Domestic)", value: "terminal-1-domestic" },
  { label: "Terminal 2 (Domestic)", value: "terminal-2-domestic" },
  { label: "Terminal 2 (International)", value: "terminal-2-international" },
  { label: "Terminal 3 (International)", value: "terminal-3-international" },
];

const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    height: "50px",
    borderRadius: "5px",
    border: "1px solid #e8e8e8",
    backgroundColor: "#ffffff",
    fontSize: "14px",
    boxShadow: "none",
    "&:hover": {
      border: "1px solid #e8e8e8",
    },
    fontWeight: "normal",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    paddingLeft: "14px",
    fontWeight: "normal",
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "5px",
    marginTop: "4px",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: "14px",
    backgroundColor: state.isSelected ? "#dbbe78" : state.isFocused ? "#eddfbb" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#282828",
    cursor: "pointer",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    fontWeight: "normal",
  }),
};

const AdditionalInformationServicesSection = forwardRef(({
  DEPARTURE_FLIGHT_ICON,
  pickup,
  dropoff,
  pickupType = "airport",
  dropoffType = "airport",
  pickupFlightNo,
  setPickupFlightNo,
  dropoffFlightNo,
  setDropoffFlightNo,
  pickupTerminal,
  setPickupTerminal,
  dropoffTerminal,
  setDropoffTerminal,
  terminalOptions,
  meetAssistS,
  onChangeMeetAssist,
  meetAssistPrice,
  isPickupHaneda,
  pickupLocation,
  dropoffLocation,
  isDropoffHaneda,
  isPickupUseMeetService,
  isDropoffUseMeetService,
  isPickupUseTerminalSelection,
  isDropoffUseTerminalSelection,
  pickupSignHonorific,
  setPickupSignHonorific,
  formatPickupSignHonorific,
  passengerTitleOptions,
  pickupSign,
  setPickupSign,
  vehicles,
  passengers,
  childSeatAllocations,
  incrementChildSeat,
  decrementChildSeat,
  message,
  setMessage,
  isPickupFlightInvalid = false,
  isPickupTerminalInvalid = false,
  isPickupSignInvalid = false,
  isDropoffFlightInvalid = false,
  isDropoffTerminalInvalid = false,
}, ref) => {
  const { t, language } = useBookingEditText();
  const getTypographyClass = createTypographyHelper(language);

  // What each leg's opt-out is called. Admins may always opt out -- nothing is
  // mandatory on this screen, unlike the customer pages (see
  // getAdminMeetAssistPolicy) -- so the leg is never forced onto Meet & Assist.
  const pickupPolicy = getAdminMeetAssistPolicy(pickupLocation, pickupTerminal);
  const dropoffPolicy = getAdminMeetAssistPolicy(dropoffLocation, dropoffTerminal, {
    isDropoff: true,
  });

  const getMeetAssistValue = (type) => {
    return meetAssistS?.find((item) => item?.meetAssistType === type)?.meetAssistName || "none";
  };

  const getMeetAssistPriceValue = (type) => {
    return Number(meetAssistS?.find((item) => item?.meetAssistType === type)?.meetAssistPrice || 0);
  };

  const allSeatTypes = [
    {
      key: "infant",
      title: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.infant"),
      subtitle: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.infantInfo"),
      limit: Number(passengers?.baby || 0),
    },
    {
      key: "toddler",
      title: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.toddler"),
      subtitle: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.toddlerInfo"),
      limit: Number(passengers?.toddler || 0),
    },
    {
      key: "child",
      title: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.child"),
      subtitle: t("editBooking.BookingEditPage.additionalInformation.childSeat.type.childInfo"),
      limit: Number(passengers?.child || 0),
    },
  ];

  const getVehicleSeatValue = (vehicleIndex, seatTypeKey) => {
    const allocation = childSeatAllocations?.[vehicleIndex];
    return Number(allocation?.[seatTypeKey] || 0);
  };

  const getTotalSeatAllocatedByType = (seatTypeKey) => {
    return (childSeatAllocations || []).reduce(
      (sum, allocation) => sum + Number(allocation?.[seatTypeKey] || 0),
      0
    );
  };

  const renderTransportBlock = ({
    mode,
    type,
    flightNo,
    setFlightNo,
    terminal,
    setTerminal,
    isHaneda,
    useMeetService,
    useTerminalSelection,
    policy,
    isFlightInvalid = false,
    isTerminalInvalid = false,
  }) => {
    const isAirport = type === "airport";
    const meetAssistType = mode === "Pickup" ? "arrival" : "departure";
    const meetAssistValueRaw = getMeetAssistValue(meetAssistType);
    const meetAssistValue =
      type === "station" && meetAssistValueRaw === "vipMeetAssist"
        ? "meetAssist"
        : meetAssistValueRaw;
    const numberLabel = t(`editBooking.BookingEditPage.additionalInformation.${mode}.flightNumber.${type}`);
    const numberIcon = isAirport ? DEPARTURE_FLIGHT_ICON : TRAIN_GOLD_ICON;
    const selectedTerminalOptions = isHaneda ? TERMINAL_HANEDA_OPTIONS : terminalOptions;
    const selectedTerminalValue = selectedTerminalOptions.find((option) => option.value === terminal) || null;

    const meetAssistOptions = [
      {
        value: "none",
        label: t(
          `editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistType.${OPT_OUT_TRANSLATION_KEY[policy.optOutLabel]}`
        ),
        subtitleClass: getTypographyClass(12),
      },
      {
        value: "meetAssist",
        label: t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistType.meetAssist.title`),
        subtitle: t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistType.meetAssist.price`, {
          price: (mode === "Pickup" ? meetAssistPrice?.pickup?.meetAssistBasePrice : meetAssistPrice?.dropoff?.meetAssistBasePrice || 0).toLocaleString(),
        }),
        subtitleClass: getTypographyClass(12),
      },
      isAirport && {
        value: "vipMeetAssist",
        label: t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistType.vipMeetAssist.title`),
        subtitle: t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistType.vipMeetAssist.price`, {
          price: (mode === "Pickup" ? meetAssistPrice?.pickup?.vipMeetAssistBasePrice : meetAssistPrice?.dropoff?.vipMeetAssistBasePrice || 0).toLocaleString(),
          additionalPrice: (mode === "Pickup" ? meetAssistPrice?.pickup?.vipMeetAssistAdditionalPrice : meetAssistPrice?.dropoff?.vipMeetAssistAdditionalPrice || 0).toLocaleString(),
        }),
        subtitleClass: getTypographyClass(12),
      },
    ].filter(Boolean);

    return (
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-2.5">
          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:gap-2.5">
            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-2">
              <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {numberLabel} <span className="tw:text-[#ea4335]">*</span>
              </div>
              <div className={`tw:h-[50px] tw:rounded-[5px] tw:border tw:bg-white tw:px-[14px] tw:flex tw:items-center tw:gap-2.5 ${isFlightInvalid ? "tw:border-[#C01F12]" : "tw:border-[#e8e8e8]"} ${getTypographyClass(14)}`}>
                <span
                  className="tw:h-[15px] tw:w-[22px] tw:inline-block"
                  style={{
                    backgroundColor: "#DBBE78",
                    WebkitMaskImage: `url(${numberIcon})`,
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskImage: `url(${numberIcon})`,
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    maskSize: "contain",
                  }}
                />
                <input
                  type="text"
                  placeholder={t(`editBooking.BookingEditPage.additionalInformation.${mode}.flightNumberPlaceholder.${type}`)}
                  value={flightNo}
                  onChange={(e) => setFlightNo(e.target.value)}
                  className={`tw:flex-1 tw:outline-none tw:bg-transparent placeholder:tw:text-[#a7a7a7] ${getTypographyClass(14)} tw:font-hanken-grotesk`}
                />
              </div>
              {isFlightInvalid && (
                <p className={`tw:!text-[#C01F12] tw:!text-[12px] tw:!mb-0`}>{t(`editBooking.BookingEditPage.additionalInformation.${mode}.flightNumberInvalidMessage.${type}`)}</p>
              )}
            </div>

            {isAirport && useTerminalSelection && (
              <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full tw:lg:w-[300px]">
                <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                  {t(`editBooking.BookingEditPage.additionalInformation.${mode}.terminal`)} <span className="tw:text-[#ea4335]">*</span>
                </div>
                <div className={`tw:rounded-[5px] tw:border ${isTerminalInvalid ? "tw:border-[#C01F12]" : "tw:border-[#e8e8e8]"}`}>
                  <Select
                    placeholder={t(`editBooking.BookingEditPage.additionalInformation.${mode}.terminalPlaceholder`)}
                    classNamePrefix="react-select-terminal"
                    styles={{ ...selectStyles, control: (baseStyles) => ({ ...selectStyles.control(baseStyles), border: "none" }) }}
                    autoComplete="off"
                    onChange={(option) => setTerminal(option?.value || "")}
                    value={selectedTerminalValue}
                    options={selectedTerminalOptions}
                    formatOptionLabel={(option) => t(`editBooking.BookingEditPage.additionalInformation.terminal.${option.value}`)}
                    components={{
                      DropdownIndicator: (props) => (
                        <components.DropdownIndicator {...props}>
                          <div className="tw:pr-2">
                            <GoTriangleDown color="#DBBE78" size={16} />
                          </div>
                        </components.DropdownIndicator>
                      ),
                      IndicatorSeparator: () => null,
                    }}
                  />
                </div>
                {isTerminalInvalid && (
                  <p className={`tw:!text-[#C01F12] tw:!text-[12px] tw:!mb-0`}>{t(`editBooking.BookingEditPage.additionalInformation.${mode}.terminalInvalidMessage`)}</p>
                )}
              </div>
            )}
          </div>

          <div className={`tw:m-0 tw:text-[#7c7b7b] tw:max-w-[840px] ${getTypographyClass(12)}`}>
            {t(`editBooking.BookingEditPage.additionalInformation.${mode}.flightNumberInfo.${type}`)}
          </div>
        </div>
        {useMeetService && (
          <div className="tw:flex tw:flex-col tw:gap-2.5">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
              <div className="tw:flex tw:flex-col tw:gap-1">
                <div className={`tw:text-[#212529] ${getTypographyClass(14)}`}>
                  {t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistService`)}
                </div>
                <div className={`tw:m-0 tw:text-[rgba(78,78,78,0.87)] ${getTypographyClass(12)}`}>
                  {t(`editBooking.BookingEditPage.additionalInformation.${mode}.meetAssistServiceInfo.${type}`)}
                </div>
              </div>
              <div className={`tw:text-[#282828] tw:font-medium ${getTypographyClass(14)} tw:flex-1 tw:min-w-fit`}>
                {`+${getMeetAssistPriceValue(meetAssistType).toLocaleString()}${t("editBooking.BookingEditPage.additionalInformation.jpy")}`}
              </div>
            </div>

            <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-wrap tw:gap-1.5">
              <SelectionButton
                options={meetAssistOptions}
                value={meetAssistValue}
                onChange={(value) => onChangeMeetAssist(meetAssistType, value)}
                className={getTypographyClass(14)}
                variant="large"
                fullWidth
              />
            </div>

            {isAirport && meetAssistValue === "vipMeetAssist" && (
              <div className="tw:bg-[#f9f9f9] tw:rounded-[5px] tw:px-4 tw:py-3 tw:flex tw:flex-col tw:gap-4.5 tw:text-[#282828]">
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <div className={`tw:m-0 tw:font-bold tw:uppercase tw:tracking-wide tw:font-hanken-grotesk ${getTypographyClass(10)}`}>
                    {t(`editBooking.BookingEditPage.additionalInformation.${mode}.vipMeetAssistInfo.vipAirportAssistance`)}
                  </div>
                  <div className={`tw:m-0 ${getTypographyClass(12)}`}>
                    {t(`editBooking.BookingEditPage.additionalInformation.${mode}.vipMeetAssistInfo.vipAirportAssistanceInfo`)}
                  </div>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <div className={`tw:m-0 tw:font-bold tw:uppercase ${getTypographyClass(10)}`}>
                    {t(`editBooking.BookingEditPage.additionalInformation.${mode}.vipMeetAssistInfo.cancellationPolicy`)}
                  </div>
                  <div className={`tw:m-0 ${getTypographyClass(12)}`}>
                    {t(`editBooking.BookingEditPage.additionalInformation.${mode}.vipMeetAssistInfo.cancellationPolicyInfo`)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <section ref={ref} className="tw:!mb-4 tw:!bg-white">
      <div className="tw:!px-5 tw:!pt-5 tw:!pb-2">
        <div className="tw:!mb-0 tw:!text-[#282828] tw:!text-[16px] tw:!font-[600] tw:font-hanken-grotesk">
          {t("editBooking.BookingEditPage.additionalInformation.title")}
        </div>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-6 tw:p-5">
        {pickup &&
          <>
            {
              renderTransportBlock({
                mode: "Pickup",
                type: pickupType,
                flightNo: pickupFlightNo,
                setFlightNo: setPickupFlightNo,
                terminal: pickupTerminal,
                setTerminal: setPickupTerminal,
                isHaneda: isPickupHaneda,
                useMeetService: isPickupUseMeetService,
                useTerminalSelection: isPickupUseTerminalSelection,
                policy: pickupPolicy,
                isFlightInvalid: isPickupFlightInvalid,
                isTerminalInvalid: isPickupTerminalInvalid,
              })
            }
            <div className="tw:flex tw:flex-col tw:gap-2">
              <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {t(`editBooking.BookingEditPage.additionalInformation.Pickup.pickupSign.${pickupType}`)}
              </div>
              <div className="tw:flex tw:gap-2">
                <div className="tw:w-[150px]">
                  <Select
                    placeholder={"Title"}
                    classNamePrefix="react-select-pickup-sign-title"
                    styles={selectStyles}
                    autoComplete="off"
                    onChange={(option) => setPickupSignHonorific(option?.value || "")}
                    value={formatPickupSignHonorific(pickupSignHonorific)}
                    options={passengerTitleOptions}
                    components={{
                      DropdownIndicator: (props) => (
                        <components.DropdownIndicator {...props}>
                          <div className="tw:pr-2">
                            <GoTriangleDown color="#DBBE78" size={16} />
                          </div>
                        </components.DropdownIndicator>
                      ),
                      IndicatorSeparator: () => null,
                    }}
                  />
                </div>
                <input
                  type="text"
                  placeholder={t("editBooking.BookingEditPage.additionalInformation.Pickup.pickupSignPlaceholder")}
                  value={pickupSign}
                  onChange={(e) => setPickupSign(e.target.value)}
                  className={`tw:flex-1 tw:h-[50px] tw:rounded-[5px] tw:border tw:bg-white tw:px-[14px] tw:outline-none focus:tw:border-[#282828] placeholder:tw:text-[#a7a7a7] ${isPickupSignInvalid ? "tw:border-[#C01F12]" : "tw:border-[#e8e8e8]"} ${getTypographyClass(14)} tw:font-hanken-grotesk`}
                />
              </div>
              {isPickupSignInvalid && (
                <p className={`tw:!text-[#C01F12] tw:!text-[12px] tw:!mb-0`}>{t("editBooking.BookingEditPage.additionalInformation.Pickup.pickupSignInvalidMessage")}</p>
              )}
              <div className="tw:flex tw:flex-col tw:w-full">
                <div className={`tw:m-0 tw:text-[#7c7b7b] ${getTypographyClass(12)}`}>
                  {t("editBooking.BookingEditPage.additionalInformation.Pickup.pickupSignInfo")}
                </div>
                {
                  meetAssistS[0]?.meetAssistName === "vipMeetAssist" && (
                    <div
                      className="tw:!text-[13px] tw:!m-0 tw:!text-[#EA4335]"
                    >
                      {t("editBooking.BookingEditPage.additionalInformation.Pickup.vipMeetAssitNote")}
                    </div>
                  )
                }
              </div>
            </div>
          </>
        }

        {dropoff &&
          renderTransportBlock({
            mode: "Dropoff",
            type: dropoffType,
            flightNo: dropoffFlightNo,
            setFlightNo: setDropoffFlightNo,
            terminal: dropoffTerminal,
            setTerminal: setDropoffTerminal,
            isHaneda: isDropoffHaneda,
            useMeetService: isDropoffUseMeetService,
            useTerminalSelection: isDropoffUseTerminalSelection,
            policy: dropoffPolicy,
            isFlightInvalid: isDropoffFlightInvalid,
            isTerminalInvalid: isDropoffTerminalInvalid,
          })
        }

        {vehicles && vehicles.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-3">
            <div className="tw:flex tw:items-center tw:justify-between">
              <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {t("editBooking.BookingEditPage.additionalInformation.childSeat.title")}
              </div>
              <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
                {t("editBooking.BookingEditPage.additionalInformation.childSeat.price")}
              </div>
            </div>

            {(vehicles || []).map((vehicle, index) =>
              vehicles.length > 1 ? (
                <div key={vehicle.id} className="tw:flex tw:flex-col tw:gap-2.5 tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#fcfcfc] tw:p-3">
                  <div className={`tw:text-[#282828] tw:font-medium ${getTypographyClass(13)}`}>
                    {t("editBooking.BookingEditPage.additionalInformation.childSeat.vehicle", {
                      number: index + 1,
                      vehicleName: vehicle?.selectedVehicle ? ` - ${vehicle.selectedVehicle}` : "",
                    })}
                  </div>

                  <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-2.5">
                    {allSeatTypes.map((seatType) => (
                      <CounterCard
                        key={`${vehicle.id}-${seatType.key}`}
                        title={seatType.title}
                        subtitle={seatType.subtitle}
                        value={getVehicleSeatValue(index, seatType.key)}
                        disableIncrement={false}
                        onIncrement={() => incrementChildSeat(index, seatType.key)}
                        onDecrement={() => decrementChildSeat(index, seatType.key)}
                        titleClass={getTypographyClass(13)}
                        subtitleClass={getTypographyClass(12)}
                        valueClass={getTypographyClass(13)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-2.5">
                  {allSeatTypes.map((seatType) => (
                    <CounterCard
                      key={`${vehicle.id}-${seatType.key}`}
                      title={seatType.title}
                      subtitle={seatType.subtitle}
                      value={getVehicleSeatValue(index, seatType.key)}
                      disableIncrement={false}
                      onIncrement={() => incrementChildSeat(index, seatType.key)}
                      onDecrement={() => decrementChildSeat(index, seatType.key)}
                      titleClass={getTypographyClass(13)}
                      subtitleClass={getTypographyClass(12)}
                      valueClass={getTypographyClass(13)}
                    />
                  ))}
                </div>
              )
            )}

            <div className="tw:bg-[#f9f9f9] tw:rounded-[5px] tw:px-5 tw:py-2.5 tw:flex tw:items-center">
              <div className={`tw:m-0 tw:text-[#4e4e4e] ${getTypographyClass(13)}`}>
                {t("editBooking.BookingEditPage.additionalInformation.childSeat.note")}
              </div>
            </div>
          </div>
        )}

        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className={`tw:text-[#8d8d8d] ${getTypographyClass(13)}`}>
            {t("editBooking.BookingEditPage.additionalInformation.messageInquiry")}
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("editBooking.BookingEditPage.additionalInformation.messageInquiryPlaceholder")}
            className={`tw:h-[120px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-white tw:px-[14px] tw:py-3 tw:outline-none focus:tw:border-[#282828] tw:resize-none placeholder:tw:text-[#a7a7a7] ${getTypographyClass(14)} tw:font-hanken-grotesk`}
          />
        </div>
      </div>
    </section>
  );
});

export default AdditionalInformationServicesSection;
